import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import * as installationApi from '@/api/installation.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';
import { camposFaltantesValidacion } from '@/lib/cierre-validacion';

/**
 * VALIDACIÓN DE SUPERVISIÓN — portado de silvai-chat (S-01).
 *
 * El técnico cerró en sitio (modelo + serie + resultado) y el trabajo quedó
 * "Por validar". Acá supervisión completa lo COMERCIAL —monto, forma de pago y
 * vendedor— y el trabajo queda cerrado en firme.
 *
 * Diferencia con silvai-chat: los vendedores son los miembros del NEGOCIO
 * ACTIVO (no todos los usuarios, que en core son de toda la organización).
 */
export function ValidarCierreModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [monto, setMonto] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [vendedorUserId, setVendedorUserId] = useState('');
  const [rnc, setRnc] = useState('');
  const [comprobante, setComprobante] = useState('');
  const [requiereFiscal, setRequiereFiscal] = useState(false);
  // Tipificación oficial de una garantía: arranca con lo que propuso el técnico.
  const [nivel, setNivel] = useState('');
  const [causa, setCausa] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cfg = useQuery({
    queryKey: ['installation', 'crm'],
    queryFn: installationApi.getCrmView,
    enabled: Boolean(job),
  });
  const vendedores = useQuery({
    queryKey: ['jobs', 'vendedores'],
    queryFn: jobsApi.listVendedores,
    enabled: Boolean(job),
  });

  // Al abrir sobre otro trabajo, el formulario arranca con lo que ese trabajo trae.
  useEffect(() => {
    if (!job) return;
    setMonto(job.montoCierre != null ? String(job.montoCierre) : '');
    setFormaPago(job.formaPago ?? '');
    setVendedorUserId(job.vendedorUserId ?? '');
    setRnc(job.rnc ?? '');
    setComprobante(job.comprobanteFiscal ?? '');
    setRequiereFiscal(job.requiereComprobanteFiscal ?? false);
    setNivel(job.nivelResolucion ?? job.nivelResolucionPropuesto ?? '');
    setCausa(job.causaRaiz ?? job.causaRaizPropuesta ?? '');
    setError(null);
  }, [job]);

  const esGarantia = job?.esGarantia ?? false;
  const formasPago = (cfg.data?.trabajos?.formasPago ?? []).filter((f) => f.activa);
  const niveles = (cfg.data?.trabajos?.nivelesResolucion ?? []).filter((n) => n.activo);
  const causas = (cfg.data?.trabajos?.causasRaiz ?? []).filter((c) => c.activo);
  const montoNumero = monto.trim() === '' ? null : Number(monto);

  const faltan = camposFaltantesValidacion({
    esGarantia,
    requiereComprobanteFiscal: requiereFiscal,
    montoCierre: montoNumero,
    formaPago,
    vendedorUserId,
    rnc,
    comprobanteFiscal: comprobante,
  });

  function close() {
    setError(null);
    onClose();
  }

  const validar = useMutation({
    mutationFn: async () => {
      // La casilla fiscal es un dato DEL TRABAJO: si se cambió acá, se guarda
      // antes de validar (si no, el servidor la vería como estaba).
      if (job && requiereFiscal !== (job.requiereComprobanteFiscal ?? false)) {
        await jobsApi.setComprobanteFiscal(job.id, requiereFiscal);
      }
      return jobsApi.validarCierre(job!.id, {
        montoCierre: esGarantia ? null : montoNumero,
        formaPago: esGarantia ? null : formaPago.trim() || null,
        vendedorUserId: esGarantia ? null : vendedorUserId || null,
        rnc: rnc.trim() || null,
        comprobanteFiscal: comprobante.trim() || null,
        nivelResolucion: esGarantia ? nivel || null : null,
        causaRaiz: esGarantia ? causa || null : null,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Trabajo cerrado');
      close();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo cerrar el trabajo'),
  });

  function submit() {
    if (faltan.length > 0) {
      setError(`Antes de cerrar falta ${faltan.join(', ')}.`);
      return;
    }
    if (esGarantia && (!nivel || !causa)) {
      setError('Confirme cómo se resolvió la garantía y por qué falló.');
      return;
    }
    setError(null);
    validar.mutate();
  }

  const cliente = job?.contact?.name ?? job?.contact?.pushName ?? job?.contact?.phone ?? 'el cliente';
  const selectClass =
    'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

  return (
    <Modal
      open={Boolean(job)}
      onClose={close}
      title="Cerrar el trabajo"
      description={`El técnico ya terminó donde ${cliente}. Complete el cobro para cerrarlo.`}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={validar.isPending} disabled={faltan.length > 0}>
            <ShieldCheck className="mr-1 h-4 w-4" />
            Cerrar el trabajo
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Lo que reportó el técnico: contexto, no se edita acá. */}
        <div className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800">
          <p className="font-medium text-slate-700 dark:text-slate-200">Lo que reportó el técnico</p>
          <p className="text-slate-600 dark:text-slate-300">
            Modelo: <strong>{job?.modelo || '—'}</strong> · Serie: <strong>{job?.numeroSerie || '—'}</strong>
          </p>
          {job?.resultado && <p className="mt-1 text-slate-600 dark:text-slate-300">{job.resultado}</p>}
        </div>

        {esGarantia ? (
          <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
            Este trabajo es una <strong>garantía</strong>: no lleva monto, forma de pago ni vendedor porque no
            hay venta.
          </p>
        ) : (
          <>
            <Input
              label="Monto cobrado"
              type="number"
              min={0}
              placeholder="0"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
            />

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Forma de pago</span>
              <select className={selectClass} value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                <option value="">Elija la forma de pago…</option>
                {formasPago.map((f) => (
                  <option key={f.id} value={f.nombre}>
                    {f.nombre}
                  </option>
                ))}
              </select>
              {formasPago.length === 0 && !cfg.isLoading && (
                <span className="mt-1 block text-xs text-danger">
                  No hay formas de pago encendidas. Cárguelas en Configuración → Trabajos.
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Vendedor</span>
              <select
                className={selectClass}
                value={vendedorUserId}
                onChange={(e) => setVendedorUserId(e.target.value)}
              >
                <option value="">Elija el vendedor…</option>
                {(vendedores.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {esGarantia && (
          <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Tipificación de la garantía</p>
            <p className="text-xs text-slate-500">
              Viene con lo que propuso el técnico. Corríjalo si hace falta: el reporte usa lo que quede acá, y lo
              que él propuso se conserva aparte.
            </p>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">¿Cómo se resolvió?</span>
              <select className={selectClass} value={nivel} onChange={(e) => setNivel(e.target.value)}>
                <option value="">Elija una opción…</option>
                {niveles.map((n) => (
                  <option key={n.id} value={n.codigo}>
                    {n.nombre}
                  </option>
                ))}
              </select>
              {job?.nivelResolucionPropuesto && job.nivelResolucionPropuesto !== nivel && (
                <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                  El técnico había propuesto: {job.nivelResolucionPropuesto}
                </span>
              )}
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">¿Por qué falló?</span>
              <select className={selectClass} value={causa} onChange={(e) => setCausa(e.target.value)}>
                <option value="">Elija una opción…</option>
                {causas.map((c) => (
                  <option key={c.id} value={c.nombre}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              {job?.causaRaizPropuesta && job.causaRaizPropuesta !== causa && (
                <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                  El técnico había propuesto: {job.causaRaizPropuesta}
                </span>
              )}
            </label>
          </div>
        )}

        <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Toggle checked={requiereFiscal} label="El cliente pidió comprobante fiscal" onChange={setRequiereFiscal} />
            <span className="text-sm text-slate-700 dark:text-slate-200">El cliente pidió comprobante fiscal</span>
          </div>
          {requiereFiscal && (
            <div className="mt-3 space-y-3">
              <Input label="RNC del cliente" value={rnc} onChange={(e) => setRnc(e.target.value)} />
              <Input
                label="Número de comprobante fiscal"
                value={comprobante}
                onChange={(e) => setComprobante(e.target.value)}
              />
            </div>
          )}
        </div>

        {faltan.length > 0 && <p className="text-sm text-slate-500">Falta {faltan.join(', ')}.</p>}
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
